import { SxProps } from '@mui/material'
import { DatePicker, LocalizationProvider, DateTimePicker as MuiDateTimePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { format } from 'date-fns'
import { enCA, frCA } from 'date-fns/locale'
import { useCallback } from 'react'
import { Control, FieldError, FieldValues, Path, useController } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import translateErrorCode from './TranslateError'

interface DateTimePickerProps<T extends FieldValues = FieldValues> {
  error: FieldError | undefined
  name: Path<T>
  label: string
  control: Control<T>
  disableFuture?: boolean
  required?: boolean
  disablePast?: boolean
}

export const DateTimePicker = <T extends FieldValues = FieldValues>({
  name,
  control,
  label,
  disableFuture,
  required,
  disablePast,
}: DateTimePickerProps<T>) => {
  const {
    field: { onChange, onBlur, ref, value },
    fieldState: { error },
  } = useController({ name, control })
  const { i18n } = useTranslation()

  const changeHandler = useCallback(
    (newDate: Date | null) => {
      const triggerEvent = { target: { name, value: '' } }
      if (newDate) {
        try {
          triggerEvent.target.value = format(newDate, 'yyyy-MM-dd')
        } catch {
          triggerEvent.target.value = newDate.toString()
        }
      }
      onChange(triggerEvent)
    },
    [name, onChange],
  )

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={i18n.language === 'fr' ? frCA : enCA}>
      <MuiDateTimePicker
        views={['year', 'day', 'hours']}
        label={label}
        value={value}
        onChange={changeHandler}
        onAccept={onBlur}
        disableFuture={disableFuture}
        disablePast={disablePast}
        format="yyyy-MM-dd HH:mm"
        reduceAnimations
        ampm={false}
        slotProps={{
          textField: {
            ref: ref,
            value: value,
            onBlur: onBlur,
            error: error !== undefined,
            helperText: error ? translateErrorCode(error) : '',
            required: required,
            fullWidth: true,
          },
        }}
      />
    </LocalizationProvider>
  )
}

DateTimePicker.default = {
  required: false,
  disablePast: false,
  disableFuture: false,
}

interface DatePickerProps<T extends FieldValues = FieldValues> {
  error: FieldError | undefined
  name: Path<T>
  label: string
  control: Control<T>
  disableFuture?: boolean
  shouldDisableDate?: (date: Date) => boolean
  required?: boolean
  disablePast?: boolean
  minDate?: Date
  maxDate?: Date
}

export const DatePickerInput = <T extends FieldValues = FieldValues>({
  name,
  control,
  label,
  disableFuture,
  error,
  disablePast,
  shouldDisableDate,
  minDate,
  maxDate,
  ...props
}: DatePickerProps<T>) => {
  const {
    field: { onChange, onBlur, ref, value },
  } = useController({ name, control })

  const changeHandler = useCallback(
    (newDate: Date | null) => {
      const triggerEvent = { target: { name, value: '' } }
      if (newDate) {
        try {
          triggerEvent.target.value = format(newDate, 'yyyy-MM-dd')
        } catch {
          triggerEvent.target.value = newDate.toString()
        }
      }
      onChange(triggerEvent)
    },
    [name, onChange],
  )

  const date = new Date(`${value}T23:59:59.000Z`)

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DatePicker
        label={label}
        format="yyyy-MM-dd"
        reduceAnimations
        disableFuture={disableFuture}
        disablePast={disablePast}
        shouldDisableDate={shouldDisableDate}
        onChange={changeHandler}
        onAccept={onBlur}
        minDate={minDate}
        maxDate={maxDate}
        slotProps={{
          textField: {
            name: name,
            onBlur: onBlur,
            error: Boolean(error),
            helperText: error ? translateErrorCode(error) : '',
            value: date,
            fullWidth: true,
          },
        }}
        {...props}
        value={date}
        ref={ref}
      />
    </LocalizationProvider>
  )
}

DatePickerInput.default = {
  required: false,
  disablePast: false,
  disableFuture: false,
  minDate: new Date(0),
  shouldDisableDate: undefined,
}

interface SimpleDatePickerProps<T extends FieldValues = FieldValues> {
  label: string
  sx: SxProps
  name: Path<T>
  control: Control<T>
}

export const SimpleDatePicker = <T extends FieldValues = FieldValues>({
  label,
  sx,
  name,
  control,
}: SimpleDatePickerProps<T>) => {
  const {
    field: { onChange, value },
  } = useController({ name, control })

  const changeHandler = useCallback(
    (newDate: Date | null) => {
      const triggerEvent = { target: { name, value: '' } }
      if (newDate) {
        try {
          triggerEvent.target.value = format(newDate, 'yyyy-MM-dd')
        } catch {
          triggerEvent.target.value = newDate.toString()
        }
      }
      onChange(triggerEvent)
    },
    [name, onChange],
  )

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DatePicker
        sx={sx}
        label={label}
        value={new Date(`${value}T23:59:59.000Z`)}
        onChange={changeHandler}
        format="yyyy-MM-dd"
      />
    </LocalizationProvider>
  )
}

export { DatePickerInput as DatePicker }
export default DateTimePicker
